import React, { useRef, useState } from 'react';
import { Icon, ICONS } from 'components/Icons';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';
import SyllabusUploadProgress from './SyllabusUploadProgress';

const ACCEPTED_TYPES = ['.pdf', '.doc', '.docx', '.csv'];
const MAX_SIZE_MB = 10;

export default function SyllabusUploadStep1({
  onAnalyze,
  onSkip,
  onCancel,
  onContinue,
  loading,
  uploadProgress,
  uploadUiState,
  uploadError,
  onResetUpload,
  initialScreen = 'decision',
}) {
  const [screen, setScreen] = useState(initialScreen);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [guidance, setGuidance] = useState('');
  const inputRef = useRef(null);

  const validateFile = (f) => {
    if (!f) return false;
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    if (!ACCEPTED_TYPES.includes(ext)) {
      setError('Please upload a PDF, Word, or CSV file.');
      return false;
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`File must be under ${MAX_SIZE_MB}MB.`);
      return false;
    }
    setError('');
    return true;
  };

  const handleFile = (f) => {
    if (validateFile(f)) setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleBackToUpload = () => {
    setFile(null);
    setError('');
    setScreen('decision');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onResetUpload();
  };

  const showStatusScreen = uploadUiState !== 'idle';

  return (
    <div className="syllabus-step">
      <div className="main-con">
        {showStatusScreen ? (
          <>
            <div className="section-con">
              <div className="syllabus-processing">
                <SyllabusUploadProgress
                  currentStep={uploadProgress?.currentStep}
                  eventName={uploadProgress?.eventName}
                  estimatedSecondsRemaining={
                    uploadProgress?.estimatedSecondsRemaining
                  }
                  uploadUiState={uploadUiState}
                  uploadError={uploadError}
                  onSkip={onSkip}
                />
              </div>
            </div>
            {uploadUiState === 'analyzing' && (
              <div className="syllabus-step-footer">
                <div />
                <div
                  className="d-flex align-items-center"
                  style={{ gap: '1rem' }}
                >
                  <RectangularButton darkButton onClick={onContinue}>
                    Minimize
                  </RectangularButton>
                </div>
              </div>
            )}
            {uploadUiState === 'error' && (
              <div className="syllabus-step-footer syllabus-step-footer-centered">
                <RectangularButton onClick={handleBackToUpload}>
                  Back
                </RectangularButton>
              </div>
            )}
          </>
        ) : (
          <>
            {screen === 'decision' ? (
              <>
                <div className="modal-header">
                  <div className="syllabus-step-title">
                    Set up your lecture schedule
                  </div>
                  <p className="syllabus-step-desc">
                    How would you like to add your lecture schedule?
                  </p>
                </div>
                <div className="section-con">
                  <button
                    type="button"
                    className="syllabus-choice-card"
                    onClick={() => setScreen('upload')}
                  >
                    <span className="syllabus-choice-icon">
                      <Icon icon={ICONS.UPLOAD_FILE} size={25} />
                    </span>
                    <span className="syllabus-choice-copy">
                      <span className="syllabus-choice-title">
                        Upload a syllabus file
                      </span>
                      <span className="syllabus-choice-desc">
                        PDF, Word, or CSV — we&apos;ll extract your lecture
                        dates and topics
                      </span>
                    </span>
                    <span className="syllabus-choice-arrow">
                      <Icon icon={ICONS.CHEVRON_RIGHT} />
                    </span>
                  </button>
                  <button
                    type="button"
                    className="syllabus-choice-card"
                    onClick={onSkip}
                  >
                    <span className="syllabus-choice-icon">
                      <Icon icon={ICONS.PENCIL} size={20} />
                    </span>
                    <span className="syllabus-choice-copy">
                      <span className="syllabus-choice-title">Manual</span>
                      <span className="syllabus-choice-desc">
                        Add your lecture dates and topics one by one
                      </span>
                    </span>
                    <span className="syllabus-choice-arrow">
                      <Icon icon={ICONS.CHEVRON_RIGHT} />
                    </span>
                  </button>
                </div>
                <div className="syllabus-step-footer">
                  <RectangularButton onClick={onCancel}>
                    Cancel
                  </RectangularButton>
                </div>
              </>
            ) : (
              <>
                <div className="modal-header">
                  <div className="syllabus-step-title">
                    Set up your lecture schedule
                  </div>
                  <p className="syllabus-step-desc">
                    Every Sunday, AverPoint will suggest news articles relevant
                    to your weekly lectures. Please upload a syllabus that
                    includes dates and lecture topics — or a simple table with
                    columns for date and lecture topic.
                  </p>
                </div>
                <div className="section-con">
                  <div
                    className={`syllabus-dropzone${!file && dragOver ? ' drag-over' : ''}${file ? ' file-selected' : ''}`}
                    onDragOver={(e) => {
                      if (file) return;
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={file ? undefined : handleDrop}
                    onClick={() =>
                      !file && inputRef.current && inputRef.current.click()
                    }
                  >
                    <input
                      ref={inputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.csv"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFile(e.target.files[0])}
                    />
                    <div className="dropzone-icon">
                      <Icon icon={ICONS.UPLOAD_FILE} />
                    </div>
                    {file ? (
                      <div className="dropzone-filename font-weight-600 d-flex align-items-center justify-content-center">
                        {file.name}
                        <Icon
                          icon={ICONS.CLOSE}
                          className="ml-3 cursor-pointer av-red"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFile(null);
                            if (inputRef.current) inputRef.current.value = '';
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="file-upload-title">
                          Drop your syllabus or topic list here
                        </div>
                        <div className="dropzone-hint">
                          PDF, Word, or CSV · max {MAX_SIZE_MB} MB
                        </div>
                        <div className="dropzone-browse av-blue cursor-pointer">
                          Browse files
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {error && <div className="syllabus-error mt-2">{error}</div>}

                <div className="syllabus-guidance-section">
                  <label
                    className="syllabus-guidance-label"
                    htmlFor="syllabus-guidance"
                  >
                    Any advice on reading your syllabus?{' '}
                    <span className="syllabus-guidance-optional">
                      (optional)
                    </span>
                  </label>
                  <label className="font-size-12 av-gray font-weight-normal">
                    If your syllabus is complicated, help us understand what
                    page(s) include lecture dates, topics, and descriptions. If
                    there's a complicated table, include some instructions on
                    how to read it.
                  </label>
                  <textarea
                    id="syllabus-guidance"
                    className="syllabus-guidance-textarea"
                    placeholder="Describe anything unusual about your syllabus format that would help us parse it correctly…"
                    value={guidance}
                    onChange={(e) => setGuidance(e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="syllabus-step-footer">
                  <RectangularButton
                    onClick={() => {
                      setFile(null);
                      setError('');
                      setScreen('decision');
                    }}
                    disabled={loading}
                  >
                    Back
                  </RectangularButton>
                  <RectangularButton
                    darkButton
                    disabled={!file || loading}
                    onClick={() => onAnalyze(file, guidance)}
                  >
                    {loading ? 'Processing...' : 'Upload'}
                  </RectangularButton>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
